import React, { useCallback, useMemo, useState } from 'react';

import { compareBoardColumns } from '@aline/common';
import { link } from '@aline/routes';
import type {
  Announcements,
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import { DndContext, MeasuringStrategy, pointerWithin, rectIntersection } from '@dnd-kit/core';
import { CircularProgress, IconButton, Typography } from '@mui/material';
import { Pencil, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import type { BoardLocalState } from 'src/components/workflow/board/Board';
import { ProgressiveColumnIcon } from 'src/components/workflow/board/settings/ColumnItem';
import { UnparkConfirmDialog } from 'src/components/workflow/use-workflow-column-change-guard';
import { Apollo } from 'src/gql/generated';
import type { ReactSetStateTuple } from 'src/types';
import { makeStyles } from 'tss-react/mui';

import { BoardColumn, columnWidthPx } from './BoardColumn';
import { CreateWorkflowDialog } from './CreateWorkflowDialog';
import {
  getWorkflowFromDragData,
  OpenTasksDoneConfirmDialog,
  StartWorkConfirmDialog,
  useBoardDragAndDrop,
} from './hooks';
import { parseColumnIdFromDndId } from './utils/board-dnd-ids';
import { WorkflowCardOverlay } from './WorkflowCardOverlay';

/**
 * Accessibility announcements for screen readers during drag operations.
 * Includes position-aware messages for better screen reader UX.
 */
function createAccessibilityAnnouncements(
  columns: Pick<Apollo.Column, 'id' | 'name'>[]
): Announcements {
  const getColumnName = (id: string | number) => {
    const columnId = parseColumnIdFromDndId(String(id)) ?? String(id);
    return columns.find(c => c.id === columnId)?.name ?? 'column';
  };

  return {
    onDragCancel() {
      return 'Dragging cancelled. Workflow returned to original position.';
    },
    onDragEnd({ active, over }: DragEndEvent) {
      if (!over) return 'Workflow dropped outside of a column.';
      const workflow = getWorkflowFromDragData(active.data.current);
      const columnName = getColumnName(over.id);
      return `${workflow?.name ?? 'Workflow'} was dropped in ${columnName}.`;
    },
    onDragOver({ over }: DragOverEvent) {
      if (!over) return;
      const columnName = getColumnName(over.id);
      return `Over ${columnName}.`;
    },
    onDragStart({ active }: DragStartEvent) {
      const workflow = getWorkflowFromDragData(active.data.current);
      const columnId = workflow?.column?.id;
      const columnName = columnId
        ? (columns.find(c => c.id === columnId)?.name ?? 'column')
        : 'column';
      return `Picked up ${workflow?.name ?? 'workflow'} in ${columnName}. Use arrow keys to move between columns.`;
    },
  };
}

/**
 * Hook for managing board data fetching and column sorting logic.
 */
function useBoardColumns(selectedBoardId: string | undefined) {
  const {
    data: boardData,
    error,
    loading,
  } = Apollo.useGetBoardQuery({
    skip: !selectedBoardId,
    variables: { id: selectedBoardId ?? '' },
  });

  const board = boardData?.board;

  const sortedColumns = useMemo(() => {
    if (!board?.columns) return [];
    return [...board.columns].sort(compareBoardColumns);
  }, [board?.columns]);

  // Get columns in the Started category for progressive icon calculation
  const startedColumns = useMemo(() => {
    return sortedColumns.filter(c => c.category === Apollo.ColumnCategory.Started);
  }, [sortedColumns]);

  // Helper to calculate position within Started category
  const getStartedColumnPosition = useCallback(
    (columnId: string) => ({
      indexInStarted: startedColumns.findIndex(c => c.id === columnId) + 1,
      totalInStarted: startedColumns.length,
    }),
    [startedColumns]
  );

  return {
    board,
    error,
    getStartedColumnPosition,
    loading,
    sortedColumns,
  };
}

const useStyles = makeStyles<void, 'headerActions'>()((theme, _params, classes) => ({
  columnHeader: {
    '&:hover': {
      boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.08)',
    },
    [`&:hover .${classes.headerActions}`]: {
      opacity: 1,
      visibility: 'visible',
    },
    alignItems: 'center',
    background: '#FFF',
    border: '1px solid #E8E8E8',
    borderRadius: 16,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    height: 48,
    justifyContent: 'space-between',
    maxWidth: columnWidthPx,
    minWidth: columnWidthPx,
    padding: theme.spacing(1, 2),
    transition: 'all 0.2s ease-in-out',
  },
  columnHeaders: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(2),
  },
  columnIcon: {
    border: 'none',
    height: 24,
    marginRight: 0,
    minWidth: 24,
    width: 24,
  },
  columnName: {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    gap: theme.spacing(1),
  },
  columnsBody: {
    display: 'flex',
    flexGrow: 1,
    gap: theme.spacing(2),
    minHeight: 0,
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
    visibility: 'hidden',
  },
  loading: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 400,
  },
}));

interface BoardBodyProps {
  boardStateTuple: ReactSetStateTuple<BoardLocalState>;
  selectedBoardId: string | undefined;
  /** Ref to the scrollable board container for scroll position preservation */
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
}

export function BoardBody({
  boardStateTuple,
  scrollContainerRef,
  selectedBoardId,
}: BoardBodyProps): JSX.Element {
  const { classes } = useStyles();
  const [boardState] = boardStateTuple;
  const [createDialogColumnId, setCreateDialogColumnId] = useState<string | null>(null);
  const { board, error, getStartedColumnPosition, loading, sortedColumns } =
    useBoardColumns(selectedBoardId);

  const {
    activeWorkflow,
    cancelDoneMove,
    cancelStartWork,
    cancelUnparkMove,
    confirmDoneMove,
    confirmStartWork,
    confirmUnparkMove,
    handleDragCancel,
    handleDragEnd,
    handleDragStart,
    isDragDisabled,
    pendingDoneMove,
    pendingUnparkMove,
    sensors,
    startWorkConfirmOpen,
  } = useBoardDragAndDrop({ board, scrollContainerRef });

  const accessibilityAnnouncements = useMemo(
    () => createAccessibilityAnnouncements(sortedColumns),
    [sortedColumns]
  );

  // Prioritize pointer position, then rectangle intersection; cancel when neither hits.
  const collisionDetection: CollisionDetection = useCallback(args => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;

    const rectCollisions = rectIntersection(args);
    if (rectCollisions.length > 0) return rectCollisions;

    return [];
  }, []);

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  if (!board) {
    return (
      <div className={classes.loading}>
        <Typography variant="body1">
          {error ? 'Unable to load this board.' : 'Board not found.'}
        </Typography>
      </div>
    );
  }

  return (
    <>
      <DndContext
        accessibility={{
          announcements: accessibilityAnnouncements,
          screenReaderInstructions: {
            draggable:
              'Press tab to navigate to a workflow card. Once focused, press space or enter to pick it up. Use arrow keys to move between columns. Press space or enter to drop, or escape to cancel.',
          },
        }}
        autoScroll={{ layoutShiftCompensation: false }}
        collisionDetection={collisionDetection}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
        sensors={sensors}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
      >
        <div className={classes.columnHeaders}>
          {sortedColumns.map(column => {
            const { indexInStarted, totalInStarted } = getStartedColumnPosition(column.id);
            return (
              <ColumnHeader
                key={column.id}
                boardId={board.id}
                boardName={board.name}
                classes={classes}
                column={column}
                indexInStarted={indexInStarted}
                totalInStarted={totalInStarted}
                onAddClick={() => setCreateDialogColumnId(column.id)}
              />
            );
          })}
        </div>
        <div className={classes.columnsBody}>
          {sortedColumns.map(column => (
            <BoardColumn
              key={column.id}
              boardId={selectedBoardId}
              boardState={boardState}
              category={column.category}
              columnId={column.id}
              isDragDisabled={isDragDisabled}
            />
          ))}
        </div>
        <WorkflowCardOverlay activeWorkflow={activeWorkflow} />
        <CreateWorkflowDialog
          initialBoardId={selectedBoardId}
          initialColumnId={createDialogColumnId ?? undefined}
          open={Boolean(createDialogColumnId)}
          onClose={() => setCreateDialogColumnId(null)}
        />
      </DndContext>
      <StartWorkConfirmDialog
        open={startWorkConfirmOpen}
        onCancel={cancelStartWork}
        onConfirm={confirmStartWork}
      />
      <UnparkConfirmDialog
        currentColumnName={pendingUnparkMove?.workflow.column.name}
        open={Boolean(pendingUnparkMove)}
        targetColumnName={pendingUnparkMove?.targetColumn.name}
        onClose={cancelUnparkMove}
        onConfirm={confirmUnparkMove}
      />
      <OpenTasksDoneConfirmDialog
        pendingMove={pendingDoneMove}
        onCancel={cancelDoneMove}
        onConfirm={confirmDoneMove}
      />
    </>
  );
}

interface ColumnHeaderProps {
  boardId: string;
  boardName: string;
  classes: ReturnType<typeof useStyles>['classes'];
  column: Pick<Apollo.Column, 'id' | 'category' | 'color' | 'name' | 'sortKey'>;
  /** Position of this column within Started category (1-indexed), 0 if not Started */
  indexInStarted: number;
  /** Total number of columns in Started category */
  totalInStarted: number;
  onAddClick?: () => void;
}

function ColumnHeader({
  boardId,
  boardName,
  classes,
  column,
  indexInStarted,
  onAddClick,
  totalInStarted,
}: ColumnHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddClick?.();
  };
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(link.WorkflowBoardSettings({ boardId }));
  };

  return (
    <div className={classes.columnHeader}>
      <div className={classes.columnName}>
        <ProgressiveColumnIcon
          category={column.category}
          className={classes.columnIcon}
          color={column.color}
          iconSize={18}
          index={indexInStarted}
          showBorder={false}
          total={totalInStarted}
        />
        <Typography noWrap variant="subtitle1">
          {column.name}
        </Typography>
      </div>
      <div className={classes.headerActions}>
        <AlineTooltip arrow title={`Edit "${boardName}" board`}>
          <IconButton
            aria-label={`Edit "${boardName}" board`}
            size="small"
            onClick={handleEditClick}
          >
            <Pencil size={18} />
          </IconButton>
        </AlineTooltip>
        <AlineTooltip arrow title={`Add workflow to "${column.name}"`}>
          <IconButton
            aria-label={`Add workflow to "${column.name}"`}
            size="small"
            onClick={handleAddClick}
          >
            <Plus size={20} />
          </IconButton>
        </AlineTooltip>
      </div>
    </div>
  );
}
